import {useCallback, useContext} from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import {alpha, useTheme} from '@mui/material/styles';
import {useTranslation} from 'react-i18next';
import {useStore} from 'reactflow';
import {NodeActionsContext} from './NodeActionsContext';

/** Class the node body hovers on to lift every stub out of its resting state. */
export const APPEND_BUTTON_CLASS = 'automation-append';

const CONTROL_SIZE = 24;

const STUB_LENGTH = 16;

const STUB_THICKNESS = 2;

const RESTING_OPACITY = 0.45;

interface NodeAppendButtonProps {
    nodeId: string;
    color: string;
    /** The If step's `true` / `false`; absent on a node with one source handle. */
    handleId?: string;
    /** Matches the handle offset on nodes with more than one source (`30%` / `70%`). */
    offset?: string;
}

const NodeAppendButton = ({nodeId, color, handleId, offset}: NodeAppendButtonProps) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const actions = useContext(NodeActionsContext);

    const active = actions?.activeAppend?.nodeId === nodeId
        && actions?.activeAppend?.sourceHandle === (handleId ?? null);

    // A one-output node owns every edge leaving it, whatever id it stores (the API saves "default").
    // An If edge counts only on its "true" / "false" handle: the engine follows nothing else.
    const connected = useStore(useCallback(
        state => state.edges.some(edge => edge.source === nodeId && (!handleId || edge.sourceHandle === handleId)),
        [nodeId, handleId]
    ));

    const connecting = useStore(state => Boolean(state.connectionStartHandle));

    if (!actions || actions.readOnly || connected || connecting) return null;

    return (
        <Box
            className={`${APPEND_BUTTON_CLASS} nodrag nopan`}
            sx={{
                position: 'absolute',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                pointerEvents: 'none',
                zIndex: 2,
                top: 'calc(100% + 7px)',
                left: offset ?? '50%',
                transform: 'translateX(-50%)',
                opacity: active ? 1 : RESTING_OPACITY,
                transition: 'opacity 140ms ease-out',
                '&:hover': {opacity: 1},
            }}
        >
            <Box
                sx={{
                    backgroundColor: alpha(color, active ? 1 : 0.5),
                    transition: 'background-color 140ms ease-out',
                    pointerEvents: 'none',
                    width: STUB_THICKNESS,
                    height: STUB_LENGTH,
                }}
            />

            <Paper
                elevation={2}
                sx={{
                    borderRadius: '50%',
                    border: `1px solid ${active ? color : theme.palette.divider}`,
                    boxShadow: active
                        ? `${theme.shadows[2]}, 0 0 0 5px ${alpha(color, 0.22)}`
                        : undefined,
                    lineHeight: 0,
                    pointerEvents: 'auto',
                    transition: 'box-shadow 140ms ease-out, border-color 140ms ease-out',
                }}
            >
                <Tooltip title={t('Add next step')}>
                    <IconButton
                        size={'small'}
                        aria-label={t('Add next step')}
                        sx={{
                            width: CONTROL_SIZE,
                            height: CONTROL_SIZE,
                            transition: 'background-color 140ms ease-out, color 140ms ease-out',
                            ...(active ? {
                                backgroundColor: color,
                                color: theme.palette.getContrastText(color),
                                '&:hover': {backgroundColor: color},
                            } : {
                                // Transparent, not a tint: the button keeps the Paper's white behind it.
                                '&:hover': {backgroundColor: 'transparent', color},
                            }),
                        }}
                        onClick={event => {
                            event.stopPropagation();
                            actions.onAppendClick(nodeId, handleId ?? null, event.currentTarget);
                        }}
                    >
                        <AddIcon sx={{fontSize: 16}}/>
                    </IconButton>
                </Tooltip>
            </Paper>
        </Box>
    );
};

export default NodeAppendButton;
